// DocumentsPage — Client → Project → Folder-tree browser (DocumentBrowser) with the
// classic flat "all documents" table preserved when no project is selected. The flat
// table is a merged cross-project LIBRARY (Oct 2026 feedback Prompt 9): real Documents
// + completed CDM/Inspection form records + submitted Weekly Site Reports, filterable
// by Project/date next to Category, with multi-select "Download selected (zip)".
import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Loader2, FileText, Upload, Download, AlertTriangle, Eye, Files, FilePlus2, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import DocumentBrowser from "@/components/documents/DocumentBrowser";
import CreateReportMenu from "@/components/documents/CreateReportMenu";
import { UploadDialog, BulkUploadDialog, DocumentPreviewDialog, DocumentDetail, STATUS_TONE, formatBytes, fmtDate } from "@/components/documents/DocumentDialogs";

const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);
const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant", "client_admin", "site_manager"]);
const APPROVE_ROLES = new Set(["director", "administrator", "consultant"]);
const VIRTUAL_STATUS_LABEL = { submitted: "Submitted", reviewed: "Reviewed", accepted: "Accepted", Submitted: "Submitted", Approved: "Approved" };
const rowKey = (r) => `${r.source}:${r.id}`;

export default function DocumentsPage() {
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [projects, setProjects] = useState([]);
    const [meta, setMeta] = useState({ categories: [], statuses: [] });
    const [q, setQ] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    const [libraryProjectFilter, setLibraryProjectFilter] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [zipping, setZipping] = useState(false);
    const [loading, setLoading] = useState(true);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [uploadPrefill, setUploadPrefill] = useState(null);
    const [detail, setDetail] = useState(null);
    const [returnTo, setReturnTo] = useState(null);
    // Folder browsing selection
    const [clientSel, setClientSel] = useState("");
    const [projectSel, setProjectSel] = useState("");
    const [clientAllocs, setClientAllocs] = useState([]);
    const navigate = useNavigate();
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const [searchParams, setSearchParams] = useSearchParams();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const canApprove = user && APPROVE_ROLES.has(user.role);
    const isInternal = user && INTERNAL_ROLES.has(user.role);
    const canCreateReport = user && user.role !== "reviewer";

    const effectiveClient = (!isInternalView && portalClientId) ? portalClientId : clientSel;

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (portalClientId && !isInternalView) params.client_id = portalClientId;
            if (libraryProjectFilter) params.project_id = libraryProjectFilter;
            if (categoryFilter !== "All") params.category = categoryFilter;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const { data } = await api.get("/documents/library", { params });
            setRows(data);
            setSelected(new Set());
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load documents");
        }
        setLoading(false);
    }, [portalClientId, isInternalView, libraryProjectFilter, categoryFilter, dateFrom, dateTo]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        api.get("/documents/meta/categories").then((r) => setMeta(r.data)).catch(() => { });
        if (user && INTERNAL_ROLES.has(user.role)) api.get("/clients").then((r) => setClients(r.data)).catch(() => { });
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => { });
    }, [user]);

    // Load projects for the chosen client (for folder browsing + library Project filter)
    useEffect(() => {
        if (!effectiveClient) { setProjects([]); return; }
        api.get("/projects", { params: { client_id: effectiveClient } }).then((r) => setProjects(r.data)).catch(() => setProjects([]));
        setProjectSel("");
        setLibraryProjectFilter("");
    }, [effectiveClient]);

    // Client-wide report templates (allocated form templates) — labelled distinctly from documents.
    useEffect(() => {
        if (!effectiveClient) { setClientAllocs([]); return; }
        api.get("/template-allocations", { params: { client_id: effectiveClient } }).then((r) => setClientAllocs(r.data)).catch(() => setClientAllocs([]));
    }, [effectiveClient]);

    useEffect(() => {
        if (searchParams.get("new") === "1" && canEdit) {
            setUploadPrefill({
                project_id: searchParams.get("project") || null,
                client_id: searchParams.get("client") || null,
                project_label: searchParams.get("project_label") || null,
            });
            setUploadOpen(true);
            ["new", "project", "client", "project_label"].forEach((p) => searchParams.delete(p));
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, canEdit, setSearchParams]);

    useEffect(() => {
        const openId = searchParams.get("open");
        if (openId && rows.length) {
            const doc = rows.find((r) => r.id === openId);
            if (doc) {
                setDetail(doc);
                const back = searchParams.get("back");
                if (back) setReturnTo(back);
                searchParams.delete("open"); searchParams.delete("back");
                setSearchParams(searchParams, { replace: true });
            }
        }
    }, [rows, searchParams, setSearchParams]);

    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
    const buildingMap = Object.fromEntries(buildings.map((b) => [b.id, b]));
    const todayIso = new Date().toISOString().slice(0, 10);

    const filtered = rows.filter((r) => {
        if (statusFilter !== "All" && r.source === "document" && r.status !== statusFilter) return false;
        if (q) {
            const hay = `${r.title} ${r.filename || ""} ${(r.tags || []).join(" ")} ${r.category} ${clientMap[r.client_id]?.name || ""}`.toLowerCase();
            if (!hay.includes(q.toLowerCase())) return false;
        }
        return true;
    });
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, {
        accessors: {
            title: (r) => r.title || "", category: (r) => r.category || "",
            client: (r) => clientMap[r.client_id]?.name || "", owner: (r) => r.owner_name || "",
            review_date: (r) => r.review_date || "", expiry_date: (r) => r.expiry_date || "",
            version: (r) => r.version_number || 0, status: (r) => r.status || "",
            created_at: (r) => r.created_at || "", updated_at: (r) => r.updated_at || r.created_at || "",
        },
    });

    const counts = {
        total: rows.length,
        review: rows.filter((r) => r.status === "Under review").length,
        approved: rows.filter((r) => r.status === "Approved").length,
        expiring: rows.filter((r) => r.expiry_date && r.expiry_date <= todayIso).length,
    };

    function toggleSelectRow(r) {
        setSelected((prev) => {
            const next = new Set(prev);
            const k = rowKey(r);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    }
    function toggleSelectAll() {
        setSelected((prev) => (prev.size === sorted.length ? new Set() : new Set(sorted.map(rowKey))));
    }
    async function downloadSelectedZip() {
        const items = sorted.filter((r) => selected.has(rowKey(r))).map((r) => ({ id: r.id, source: r.source }));
        if (!items.length) return;
        setZipping(true);
        try {
            const resp = await api.post("/documents/library/zip", { items }, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
            const a = document.createElement("a");
            a.href = url; a.download = "document-library-export.zip";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
            toast.success(`Downloaded ${items.length} file(s) as a zip`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Zip download failed");
        }
        setZipping(false);
    }

    async function downloadDoc(doc, withCover = false) {
        try {
            const resp = await api.get(`/documents/${doc.id}/download`, { params: withCover ? { cover: true } : {}, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: resp.headers["content-type"] || doc.mime_type }));
            const a = document.createElement("a");
            const base = (doc.filename || `document-${doc.id.slice(0, 8)}`);
            a.href = url; a.download = withCover ? base.replace(/\.pdf$/i, "") + "-cover.pdf" : base;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
        }
    }
    const isPdf = (d) => (d.mime_type || "").toLowerCase() === "application/pdf" || (d.filename || "").toLowerCase().endsWith(".pdf");

    async function downloadLibraryRow(r) {
        if (r.source === "document") return downloadDoc(r);
        const url = r.source === "form_instance" ? `/forms/instances/${r.id}/pdf` : `/weekly-reports/${r.id}/pdf`;
        try {
            const resp = await api.get(url, { responseType: "blob" });
            const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = blobUrl; a.download = `${(r.title || "report").replace(/[^\w\- ]/g, "").slice(0, 80)}.pdf`;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(blobUrl);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
        }
    }

    const projectLabel = projects.find((p) => p.id === projectSel)?.name;

    return (
        <div data-testid="documents-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                        style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Documents &amp; Forms
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Document Library</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {counts.total} documents · <span className="font-semibold text-[#C68A1C]">{counts.review} under review</span>
                        {" · "}<span className="font-semibold text-[#3F7D3A]">{counts.approved} approved</span>
                        {counts.expiring > 0 && <> · <span className="font-semibold text-red-600">{counts.expiring} expired/expiring</span></>}
                    </p>
                </div>
                {!projectSel && (
                    <div className="flex items-center gap-2">
                        {canCreateReport && (
                            <CreateReportMenu clientId={effectiveClient || null} backTo="/app/documents" />
                        )}
                        {canEdit && (
                            <>
                                <button data-testid="document-bulk-btn" onClick={() => setBulkOpen(true)}
                                    className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg border bg-white hover:bg-[#F6F4F0]"
                                    style={{ borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                                    <Files size={15} /> Bulk upload
                                </button>
                                <button data-testid="document-upload-btn" onClick={() => setUploadOpen(true)}
                                    className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                                    style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                    <Upload size={15} /> Upload document
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Client → Project selectors (folder browsing) */}
            <div className="flex items-center gap-3 flex-wrap rounded-xl bg-white border border-[#E5E0DA] px-4 py-3" data-testid="documents-selectors">
                {isInternal && (
                    <label className="flex items-center gap-2 text-[12px] text-[#6F6A64]">
                        <span className="font-bold uppercase tracking-wide text-[10.5px]">Client</span>
                        <select data-testid="documents-client-select" value={clientSel} onChange={(e) => setClientSel(e.target.value)}
                            className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="">— All clients (flat) —</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </label>
                )}
                <label className="flex items-center gap-2 text-[12px] text-[#6F6A64]">
                    <span className="font-bold uppercase tracking-wide text-[10.5px]">Project</span>
                    <select data-testid="documents-project-select" value={projectSel} onChange={(e) => setProjectSel(e.target.value)}
                        disabled={!effectiveClient}
                        className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm disabled:bg-[#F6F4F0]">
                        <option value="">{effectiveClient ? "— Select a project to browse folders —" : "Choose a client first"}</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.reference})</option>)}
                    </select>
                </label>
                {projectSel && (
                    <button data-testid="documents-clear-project" onClick={() => setProjectSel("")}
                        className="text-[12px] text-[#9F4212] font-semibold hover:underline ml-1">Back to all documents</button>
                )}
            </div>

            {projectSel ? (
                <DocumentBrowser clientId={effectiveClient} projectId={projectSel} projectLabel={projectLabel} user={user} clients={clients} />
            ) : (
                <>
                    {effectiveClient && clientAllocs.length > 0 && (
                        <div data-testid="client-templates-section" className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                            <div className="px-4 py-3 border-b border-[#F0EDE8] flex items-center gap-2">
                                <FilePlus2 size={15} className="text-[#2E6B57]" />
                                <span className="font-semibold text-[13px] text-[#1F1F1F]">Report templates</span>
                                <span className="text-[11.5px] text-[#9A968F]">— blank templates you generate reports from (open a project to create one)</span>
                            </div>
                            <div className="divide-y divide-[#F0EDE8]">
                                {clientAllocs.map((a) => (
                                    <div key={a.id} data-testid={`client-template-row-${a.id}`} className="flex items-center gap-3 px-4 py-2.5">
                                        <FilePlus2 size={14} className="text-[#2E6B57] shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-semibold text-[13px] text-[#1F1F1F] truncate">{a.title || a.template_name}</span>
                                                <span className="text-[9.5px] uppercase font-bold px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "#E4F1EC", color: "#2E6B57" }}>Template</span>
                                            </div>
                                            <div className="text-[11.5px] text-[#9A968F] truncate">
                                                {a.folder_name} · Completed by {a.completed_by === "client" ? "Client" : "PR"} · {a.scope === "all_projects" ? "All projects" : "One project"}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A968F]" />
                            <input data-testid="document-search" placeholder="Search title, filename, tags…"
                                value={q} onChange={(e) => setQ(e.target.value)}
                                className="h-9 pl-8 pr-3 rounded-lg border border-[#E5E0DA] bg-white text-sm w-80 focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]" />
                        </div>
                        <select data-testid="document-category-filter" value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="All">All categories</option>
                            {(meta.library_categories || meta.categories).map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select data-testid="document-status-filter" value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="All">All statuses</option>
                            {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select data-testid="document-library-project-filter" value={libraryProjectFilter}
                            disabled={!effectiveClient} onChange={(e) => setLibraryProjectFilter(e.target.value)}
                            className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm disabled:bg-[#F6F4F0]">
                            <option value="">All projects</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <label className="flex items-center gap-1.5 text-[12px] text-[#6F6A64]">
                            From
                            <input type="date" data-testid="document-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                className="h-9 px-2 rounded-lg border border-[#E5E0DA] bg-white text-sm" />
                        </label>
                        <label className="flex items-center gap-1.5 text-[12px] text-[#6F6A64]">
                            To
                            <input type="date" data-testid="document-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                className="h-9 px-2 rounded-lg border border-[#E5E0DA] bg-white text-sm" />
                        </label>
                    </div>

                    {selected.size > 0 && (
                        <div data-testid="document-selection-bar" className="flex items-center gap-3 rounded-xl px-4 py-2.5" style={{ background: "#FBF3EC", border: "1px solid #EAD9C8" }}>
                            <span className="text-[13px] font-semibold text-[#1F1F1F]">{selected.size} selected</span>
                            <button data-testid="document-download-selected-zip" onClick={downloadSelectedZip} disabled={zipping}
                                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                {zipping ? <Loader2 size={13} className="animate-spin" /> : <Archive size={13} />} Download selected (zip)
                            </button>
                            <button data-testid="document-clear-selection" onClick={() => setSelected(new Set())}
                                className="text-[12px] text-[#6F6A64] hover:underline">Clear</button>
                        </div>
                    )}

                    <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F5F3F0] text-left border-b border-[#E5E0DA] group">
                                <tr>
                                    <th className="px-4 py-3 w-8">
                                        <input type="checkbox" data-testid="document-select-all-rows" checked={selected.size > 0 && selected.size === sorted.length}
                                            onChange={toggleSelectAll} className="accent-[#C75A1B]" />
                                    </th>
                                    <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="title" onClick={toggleSort}>Document</SortableTh>
                                    <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="category" onClick={toggleSort}>Category</SortableTh>
                                    <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="client" onClick={toggleSort}>Client / Building</SortableTh>
                                    <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="owner" onClick={toggleSort}>Owner</SortableTh>
                                    <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="expiry_date" onClick={toggleSort}>Review · Expiry</SortableTh>
                                    <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="created_at" onClick={toggleSort}>Date issued</SortableTh>
                                    <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="updated_at" onClick={toggleSort}>Date modified</SortableTh>
                                    <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="version" onClick={toggleSort}>v</SortableTh>
                                    <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="status" onClick={toggleSort}>Status</SortableTh>
                                    <th className="px-4 py-3 text-right"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={11} className="text-center py-10 text-[#9A968F]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</td></tr>
                                ) : sorted.length === 0 ? (
                                    <tr><td colSpan={11} className="text-center py-10 text-[#9A968F]">No documents match these filters.</td></tr>
                                ) : sorted.map((r) => {
                                    const expired = r.expiry_date && r.expiry_date <= todayIso;
                                    const isVirtual = r.source !== "document";
                                    const statusLabel = isVirtual ? (VIRTUAL_STATUS_LABEL[r.status] || r.status) : r.status;
                                    return (
                                        <tr key={rowKey(r)} data-testid={`document-row-${r.id}`}
                                            className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0] cursor-pointer"
                                            onClick={() => (isVirtual ? downloadLibraryRow(r) : setDetail(r))}>
                                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                <input type="checkbox" data-testid={`document-select-${r.id}`} checked={selected.has(rowKey(r))}
                                                    onChange={() => toggleSelectRow(r)} className="accent-[#C75A1B]" />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <FileText size={14} className="text-[#9A968F]" />
                                                    <div className="min-w-0">
                                                        <div className="font-semibold text-[#1F1F1F] truncate">{r.title}</div>
                                                        <div className="text-[11px] text-[#9A968F] truncate">
                                                            {isVirtual ? (
                                                                <span className="uppercase font-semibold text-[10px] tracking-wide">{r.source === "wsr" ? "Weekly Site Report" : "Completed form"}</span>
                                                            ) : (
                                                                <>{r.filename} · {formatBytes(r.size)}{r.tags?.length > 0 && <> · {r.tags.slice(0, 3).join(", ")}</>}</>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.category}</td>
                                            <td className="px-4 py-3 text-[#6F6A64] text-[12px]">
                                                <div>{clientMap[r.client_id]?.name || "—"}</div>
                                                {r.building_id && <div className="text-[11px]">{buildingMap[r.building_id]?.name || ""}</div>}
                                            </td>
                                            <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.owner_name || "—"}</td>
                                            <td className="px-4 py-3 text-[12px]">
                                                <div className="text-[#6F6A64]">{r.review_date || "—"}</div>
                                                <div className={expired ? "text-red-600 font-semibold flex items-center gap-1" : "text-[#9A968F]"}>
                                                    {expired && <AlertTriangle size={11} />}{r.expiry_date || "—"}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-[12px] text-[#6F6A64] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                                            <td className="px-4 py-3 text-[12px] text-[#6F6A64] whitespace-nowrap">{fmtDate(r.updated_at || r.created_at)}</td>
                                            <td className="px-4 py-3 font-mono text-[12px] text-[#6F6A64]">{r.version_number ? `v${r.version_number}` : "—"}</td>
                                            <td className="px-4 py-3"><Pill tone={STATUS_TONE[r.status] || (r.status === "Approved" || r.status === "accepted" ? "green" : "neutral")}>{statusLabel}</Pill></td>
                                            <td className="px-4 py-3 text-right whitespace-nowrap">
                                                {!isVirtual && (
                                                    <button onClick={(e) => { e.stopPropagation(); setPreviewDoc(r); }} data-testid={`document-preview-${r.id}`}
                                                        className="inline-flex items-center gap-1 text-[12px] text-[#6F6A64] hover:underline"><Eye size={12} /> Preview</button>
                                                )}
                                                <button onClick={(e) => { e.stopPropagation(); downloadLibraryRow(r); }} data-testid={`document-download-${r.id}`}
                                                    className={`${isVirtual ? "" : "ml-3 "}inline-flex items-center gap-1 text-[12px] text-[#2A5DB0] hover:underline`}><Download size={12} /> Download</button>
                                                {!isVirtual && isPdf(r) && (
                                                    <button onClick={(e) => { e.stopPropagation(); downloadDoc(r, true); }} data-testid={`document-download-cover-${r.id}`}
                                                        className="ml-3 inline-flex items-center gap-1 text-[12px] font-semibold hover:underline" style={{ color: "var(--brand-accent,#C75A1B)" }} title="Download with branded cover page">
                                                        <FileText size={12} /> + Cover
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {uploadOpen && (
                <UploadDialog clients={clients} buildings={buildings} meta={meta} prefill={uploadPrefill}
                    portalClientId={!isInternalView ? portalClientId : null}
                    onClose={() => { setUploadOpen(false); setUploadPrefill(null); }}
                    onCreated={() => { setUploadOpen(false); setUploadPrefill(null); refresh(); }} />
            )}

            {bulkOpen && (
                <BulkUploadDialog clients={clients} buildings={buildings} meta={meta}
                    prefill={{ client_id: effectiveClient || null }}
                    portalClientId={!isInternalView ? portalClientId : null}
                    onClose={() => setBulkOpen(false)}
                    onUploaded={refresh} />
            )}

            {previewDoc && (
                <DocumentPreviewDialog doc={previewDoc} onClose={() => setPreviewDoc(null)} onDownload={downloadDoc} />
            )}

            {detail && (
                <DocumentDetail doc={detail} clientMap={clientMap} buildingMap={buildingMap} canEdit={canEdit} canApprove={canApprove}
                    onClose={() => { setDetail(null); if (returnTo) { const t = returnTo; setReturnTo(null); navigate(t); } }}
                    onChanged={(d) => { setDetail(d); refresh(); }} onDeleted={() => { setDetail(null); refresh(); }}
                    onDownload={downloadDoc} onPreview={(d) => setPreviewDoc(d)} />
            )}
        </div>
    );
}
